import { Check, Plus, Scissors, Trash2, RotateCcw, RotateCw, FlipHorizontal2, Maximize2, Minimize2 } from "lucide-react";
import { ROUTE_STYLES } from "@/lib/playStyles";
import { ROUTE_PRESETS } from "@/lib/routePresets";
import { routeTiming } from "@/lib/playEngine";
import { removeLastPoint, transformRoute, deletePoint, patchPoint, setSegmentDur } from "@/lib/playOps";
import { NumIn } from "@/components/editor/fields";
import { SpeedChips } from "@/components/editor/RouteExtras";
import { RouteLibrary } from "@/components/editor/RouteLibrary";

const Chip = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} aria-pressed={on} className={`chip ${on ? "chip-on" : ""}`} data-testid={testId}>{children}</button>
);

function WaypointEditor({ play, route, idx, edit, setSelPoint }) {
  const leg = routeTiming(play, route).legs[idx];
  const pt = route.points[idx];
  if (!leg) return null;
  return (
    <div className="rounded-xl border border-[#FACC15]/50 bg-[#FACC15]/5 p-3 space-y-2" data-testid="waypoint-editor">
      <p className="text-sm font-bold">Waypoint {idx + 1} <span className="text-slate-400 font-normal">· reached at {leg.t1.toFixed(1)}s{pt.pause ? `, leaves at ${leg.tEnd.toFixed(1)}s` : ""}</span></p>
      <div className="grid grid-cols-2 gap-2">
        <NumIn edit={edit} label="Time to get here" min={0.1} max={15} value={Math.round((leg.t1 - leg.t0) * 100) / 100} onValue={(p, v) => setSegmentDur(p, route.id, idx, v)} testId="waypoint-segment-time" />
        <NumIn edit={edit} label="Pause here" max={10} value={pt.pause || 0} onValue={(p, v) => patchPoint(p, route.id, idx, { pause: v })} testId="waypoint-pause" />
      </div>
      <div className="flex flex-wrap gap-2">
        {pt.dur > 0 && <button className="btn btn-ghost" onClick={() => edit.set((p) => setSegmentDur(p, route.id, idx, null))} data-testid="waypoint-auto-time">Use route speed</button>}
        <button className="btn btn-danger" onClick={() => { edit.set((p) => deletePoint(p, route.id, idx)); setSelPoint(null); }} data-testid="btn-delete-waypoint"><Trash2 size={16} />Delete waypoint</button>
      </div>
    </div>
  );
}

export function RouteTools({ play, player, route, routeType, setRouteType, selPoint, setSelPoint, edit, onPreset, onApplyLibrary, onFinish, onNewSegment, onDeleteRoute }) {
  const tf = (kind) => edit.set((p) => transformRoute(p, route.id, kind));
  return (
    <div className="space-y-3" data-testid="route-tools">
      <div className="flex flex-wrap gap-1.5" data-testid="route-type">
        {Object.entries(ROUTE_STYLES).map(([k, s]) => <Chip key={k} on={routeType === k} onClick={() => setRouteType(k)} testId={`route-type-${k}`}>{s.label}</Chip>)}
      </div>
      <p className="text-sm text-slate-300" data-testid="tool-hint">
        {!player ? "Tap a player first. Then tap the field: every tap adds a waypoint (a cut)." : `Drawing for ${player.label}: tap the field to add waypoints. Drag a dot to move a cut, tap + to insert one, tap a dot to edit or delete it.`}
      </p>
      {player && (
        <>
          <div>
            <p className="label">Quick routes (start at {player.label})</p>
            <div className="flex flex-wrap gap-1.5" data-testid="route-presets">
              {Object.entries(ROUTE_PRESETS).map(([k, s]) => <Chip key={k} onClick={() => onPreset(k)} testId={`preset-${k}`}>{s.label}</Chip>)}
            </div>
          </div>
          <RouteLibrary play={play} player={player} onApply={onApplyLibrary} />
          {route && (
            <>
              <SpeedChips play={play} route={route} edit={edit} />
              <div className="flex flex-wrap gap-1.5" data-testid="route-curve">
                <Chip on={route.curve !== "smooth"} onClick={() => edit.set((p) => ({ ...p, routes: p.routes.map((r) => (r.id === route.id ? { ...r, curve: "sharp" } : r)) }))} testId="curve-sharp">Sharp Cut</Chip>
                <Chip on={route.curve === "smooth"} onClick={() => edit.set((p) => ({ ...p, routes: p.routes.map((r) => (r.id === route.id ? { ...r, curve: "smooth" } : r)) }))} testId="curve-smooth">Smooth Curve</Chip>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="route-transform">
                <button className="btn btn-ghost px-3" onClick={() => tf("rotL")} aria-label="Rotate left" data-testid="btn-rotate-left"><RotateCcw size={16} /></button>
                <button className="btn btn-ghost px-3" onClick={() => tf("rotR")} aria-label="Rotate right" data-testid="btn-rotate-right"><RotateCw size={16} /></button>
                <button className="btn btn-ghost px-3" onClick={() => tf("mirror")} data-testid="btn-mirror-route"><FlipHorizontal2 size={16} />Mirror</button>
                <button className="btn btn-ghost px-3" onClick={() => tf("grow")} aria-label="Make bigger" data-testid="btn-grow-route"><Maximize2 size={16} /></button>
                <button className="btn btn-ghost px-3" onClick={() => tf("shrink")} aria-label="Make smaller" data-testid="btn-shrink-route"><Minimize2 size={16} /></button>
                <button className="btn btn-ghost px-3" onClick={() => edit.set((p) => removeLastPoint(p, route.id))} data-testid="btn-remove-point"><Scissors size={16} />Last point</button>
                <button className="btn btn-danger px-3" onClick={onDeleteRoute} data-testid="btn-delete-route"><Trash2 size={16} />Route</button>
              </div>
              {selPoint?.rid === route.id && route.points[selPoint.idx] && <WaypointEditor play={play} route={route} idx={selPoint.idx} edit={edit} setSelPoint={setSelPoint} />}
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={onFinish} data-testid="btn-finish-route"><Check size={18} />Finish Route</button>
            {route && <button className="btn btn-ghost" onClick={onNewSegment} data-testid="btn-new-segment"><Plus size={16} />Add a second move after this</button>}
          </div>
        </>
      )}
    </div>
  );
}
